import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography } from '@mui/material';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import CardView from '../../component/card/card-view';
import riskOrderListState from './risk-order-list-cloud-state';
import { ConditionsBox, SearchKeyInput, SearchKeyInputItems } from '../../component/form/search-key-input.component';
import RiskOrderDetailDialog, { RiskOrderDetailDialogType } from './risk-order-detail-dialog';
import PeriodDateFilter from '../../component/period-date-filter.component';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import { dateValidate, formatRiskStatusText, validateCurrentDate, dateFormat, formatRiskMoney, formatRiskMinutes } from '../../constants/tool-order';
import moment from 'moment';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import MainPageState from '../main/main-page-states';
import { RiskOrder } from '../../models';
import RiskOrderActionBar from './risk-order-action-bar.component';
import AiAnalysisDialog from './ai-analysis-dialog.component';
import { getRiskOrderListFilterItems } from './risk-order-list-filter-items';
import { getRiskTagTextForOrder, getRiskTagsForOrder, RiskOrderGroup } from './risk-order-mode';

const mainPageData = MobxObjectsManager.get(MainPageState);

/**
 * 判断云端订单聚合项是否包含指定规则风险类型。
 * @param order - 云端风险订单聚合项
 * @param riskType - 规则风险类型编号
 * @returns 订单包含该风险类型时返回 true
 */
export function isCloudRiskTypeHighlighted(order: RiskOrderGroup, riskType: number): boolean {
    return getRiskTagsForOrder(order).some((tag) => tag.type === 'risk' && tag.value === riskType);
}

const RiskOrderListCloud: React.FC = observer(() => {
    /** 与 SearchKeyInput 的 useImperativeHandle 对齐，用于删除筛选 Chip 后重置二级下拉 */
    const searchKeyInputRef = useRef<{ setSelectKey: (val: string) => void } | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<RiskOrder | null>(null);
    useEffect(() => {
        // 初始化当前餐厅数据
        riskOrderListState.initialize(mainPageData?.currentRestaurant.string_id ?? '');
    }, [mainPageData?.currentRestaurant.string_id]);


    /** 与即时列表共用筛选项；云端订单号查询参数为 order_no */
    const filterList: SearchKeyInputItems[] = getRiskOrderListFilterItems({
        includeStatus: true,
        orderNumberKey: 'order_no',
    });

    const [viewDateTimeData, setViewDateTimeData] = useState<{
        startDate: Date | null;
        endDate: Date | null;
    }>(() => {

        const { startTime, endTime } = validateCurrentDate();
        return {
            startDate: moment(startTime, 'YYYY-MM-DD HH:mm').toDate(),
            endDate: moment(endTime, 'YYYY-MM-DD HH:mm').toDate(),
        };

    });


    /**
     * 周期/自定义日期范围变更，同步列表查询时间
     * @param dates - 选择器起止时间
     */
    const handlePeriodDateChange = (dates: [Date | null, Date | null]) => {
        setViewDateTimeData({
            startDate: dates[0],
            endDate: dates[1],
        });
        if (dates[0] && dates[1] && dateValidate(dates[0], dates[1])) {
            riskOrderListState.setDateRange(dates[0], dates[1]);
        }
    };

    const formatDateTime = (val?: string) => {
        if (!val) return '-';
        return dateFormat(val, 'HH:mm DD/MM/YYYY');
    };
    // 云端列表中数值列的高亮颜色
    const riskHighlightColor = 'var(--func-error)';

    const headCellList: HeadCell[] = [
        {
            id: 'order_head_id',
            label: i18n.t('business_analysis_page_order_account'),
            // sortStatus: riskOrderListState.sortKey === 'order_head_id' ? riskOrderListState.sortValue : 0,
        },
        {
            id: 'table_name',
            label: i18n.t('business_analysis_page_table_name'),
            // sortStatus: riskOrderListState.sortKey === 'table_name' ? riskOrderListState.sortValue : 0,
        },
        {
            id: 'people_count',
            label: i18n.t('immediate_number_people'),
            // sortStatus: riskOrderListState.sortKey === 'people_count' ? riskOrderListState.sortValue : 0,
        },
        {
            id: 'dish_count',
            label: i18n.t('intelligent_risk_control_page_column_dish_count'),
            // sortStatus: riskOrderListState.sortKey === 'dish_count' ? riskOrderListState.sortValue : 0,
        },
        {
            id: 'open_time',
            label: i18n.t('immediate_table_opening_time'),
            // sortStatus: riskOrderListState.sortKey === 'open_time' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => formatDateTime(row.open_time),
        },
        {
            id: 'close_time',
            label: i18n.t('immediate_statement_time'),
            // sortStatus: riskOrderListState.sortKey === 'close_time' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => formatDateTime(row.close_time),
        },
        {
            id: 'amount',
            label: i18n.t('intelligent_risk_control_page_column_amount_original_actual'),
            // sortStatus 仍绑定在表头 id 上，由 mobx 决定当前状态
            // sortStatus: riskOrderListState.sortKey === 'origin_amount' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => `${formatRiskMoney(row.origin_amount)} / ${formatRiskMoney(row.actual_amount)}`,
        },
        {
            id: 'discount_amount',
            label: i18n.t('intelligent_risk_control_page_column_discount_amount'),
            // sortStatus: riskOrderListState.sortKey === 'discount_amount' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => {
                const r = row as RiskOrderGroup;
                const value = formatRiskMoney(r.discount_amount);
                const highlight = isCloudRiskTypeHighlighted(r, 1) && r.discount_amount ? riskHighlightColor : undefined;
                return <span style={{ color: highlight }}>{value}</span>;
            },
        },
        {
            id: 'refund_dish_count',
            label: i18n.t('intelligent_risk_control_page_column_refund_count'),
            // sortStatus: riskOrderListState.sortKey === 'refund_dish_count' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => {
                const r = row as RiskOrderGroup;
                const highlight = isCloudRiskTypeHighlighted(r, 2) ? riskHighlightColor : undefined;
                return <span style={{ color: highlight }}>{r.refund_dish_count}</span>;
            },
        },
        {
            id: 'refund_amount',
            label: i18n.t('intelligent_risk_control_page_column_refund_amount'),
            // sortStatus: riskOrderListState.sortKey === 'refund_amount' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => {
                const r = row as RiskOrderGroup;
                const value = formatRiskMoney(r.refund_amount);
                const highlight = isCloudRiskTypeHighlighted(r, 3) && r.refund_amount ? riskHighlightColor : undefined;
                return <span style={{ color: highlight }}>{value}</span>;
            },
        },
        {
            id: 'avg_dish_per_person',
            label: i18n.t('intelligent_risk_control_page_column_avg_dish_count'),
            // sortStatus: riskOrderListState.sortKey === 'avg_dish_per_person' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => {
                const r = row as RiskOrderGroup;
                const highlight = isCloudRiskTypeHighlighted(r, 4) ? riskHighlightColor : undefined;
                return <span style={{ color: highlight }}>{r.avg_dish_per_person}</span>;
            },
        },
        {
            id: 'dining_duration_min',
            label: i18n.t('intelligent_risk_control_page_column_duration_minutes'),
            // sortStatus: riskOrderListState.sortKey === 'dining_duration_min' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => {
                const r = row as RiskOrderGroup;
                const value = formatRiskMinutes(r.dining_duration_min);
                const highlight = isCloudRiskTypeHighlighted(r, 5) ? riskHighlightColor : undefined;
                return <span style={{ color: highlight }}>{value}</span>;
            },
        },
        {
            id: 'risk_tag',
            label: i18n.t('intelligent_risk_control_page_filter_risk_tag'),
            // sortStatus: riskOrderListState.sortKey === 'risk_tag' ? riskOrderListState.sortValue : 0,
            renderFunction: (row) => (
                <span style={{ color: riskHighlightColor }}>
                    {getRiskTagTextForOrder(row as RiskOrderGroup)}
                </span>
            )
        },
        {
            id: 'status',
            label: i18n.t('restaurant_info_page_status'),
            // sortStatus: riskOrderListState.sortKey === 'status' ? riskOrderListState.sortValue : 0,
            renderFunction: (row: any) => i18n.t(formatRiskStatusText(row.orderStatus ?? row.status ?? row.risk_status)),
        },
        {
            id: 'operator_name',
            label: i18n.t('business_analysis_page_operator_person'),
            // sortStatus: riskOrderListState.sortKey === 'operator_name' ? riskOrderListState.sortValue : 0,
        },
    ];

    /** 云端列表行：展示用 status，详情弹窗统一写入 risk_status */
    type RiskOrderListRow = RiskOrder & { status?: number };

    /**
     * 点击表格行打开风控订单详情弹窗（状态、菜品等）
     * @param row - 当前行数据，与云端列表接口字段一致
     */
    const handleRowOpenDetail = (row: RiskOrderListRow) => {
        setSelectedOrder({
            ...row,
            risk_status: Number(row.orderStatus ?? row.risk_status ?? row.status ?? 0),
        });
        setDetailDialogOpen(true);
    };

    const data = riskOrderListState.data;

    // 表格排序
    const handleSort = (headCell: HeadCell) => {
        const currentStatus = (headCell as any).sortStatus ?? 0;
        riskOrderListState.changeSort(headCell.id, currentStatus as 0 | 1 | 2);
    };

    // 分页回调
    const changePageCallback = (params: { page: number; curRowsPerPage: number }) => {
        riskOrderListState.changePage(params.page + 1, params.curRowsPerPage);
    };
    return (
        <Box>
            <CardView
                title={''}
                className='mt16'
                contentRender={(
                    <>
                        {/* 标题 + 筛选 + 操作按钮：宽度不足时自动换行（对齐集团营收分析顶栏 flex-wrap） */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                rowGap: 2,
                                flexWrap: 'wrap',
                                mb: 2,
                            }}
                        >
                            <Typography variant='h3' sx={{ color: 'var(--func-sp1)', flexShrink: 0 }}>
                                {i18n.t('intelligent_risk_control_page_text_risk_order_list')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 2,
                                    rowGap: 2,
                                    flexWrap: 'wrap',
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                }}
                            >
                                <PeriodDateFilter
                                    startDate={riskOrderListState.startTime}
                                    endDate={riskOrderListState.endTime}
                                    pickerValue={[viewDateTimeData.startDate, viewDateTimeData.endDate]}
                                    onDateTimeChange={handlePeriodDateChange}
                                />
                                <SearchKeyInput
                                    ref={searchKeyInputRef}
                                    pillSelectStyle
                                    showConditionsBox={false}
                                    items={filterList}
                                    conditions={riskOrderListState.conditions}
                                    onSearch={(value) => riskOrderListState.setConditions(value)}
                                />
                                <RiskOrderActionBar
                                    exportDisabled={riskOrderListState.loading}
                                    exportPreCheck={() => {
                                        if (riskOrderListState.data.total === 0) {
                                            GlobalNotification.warning(i18n.t('general_text_no_data'));
                                            return false;
                                        }
                                        return true;
                                    }}
                                    onExport={() => riskOrderListState.downloadExcel()}
                                    onAIAnalysisClick={() => {
                                        void riskOrderListState.openAiAnalysisDialog();
                                    }}
                                />
                            </Box>
                        </Box>
                        {/* 有已选条件时展示 Chip 行，与 Card 内容区左对齐（与标题同起点），不缩进在右侧筛选项下方 */}
                        {riskOrderListState.conditions.length > 0 ? (
                            <Box sx={{ mt: 1, mb: 2, width: '100%' }}>
                                <ConditionsBox
                                    conditions={riskOrderListState.conditions}
                                    onDelete={(next) => {
                                        riskOrderListState.setConditions(next);
                                        searchKeyInputRef.current?.setSelectKey?.('');
                                    }}
                                />
                            </Box>
                        ) : null}
                        <Box>
                            <TableComponent
                                isPagination
                                isUseCustomPagination
                                allCount={data.total}
                                headCellList={headCellList}
                                list={data.list}
                                showChecked={0}
                                isTableLoading={riskOrderListState.loading}
                                handleSort={handleSort}
                                changePageCallback={changePageCallback}
                                isCurrentPageData
                                currentPage={riskOrderListState.page - 1}
                                onRowClick={(row) => handleRowOpenDetail(row as RiskOrderListRow)}
                            />
                        </Box>
                    </>
                )}
            />

            {/* 综合操作弹窗组件：状态 + 菜品详情 */}
            <RiskOrderDetailDialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                orderSummary={selectedOrder ?? undefined}
                stringId={mainPageData?.currentRestaurant.string_id}
                onSaved={() => riskOrderListState.fetchData()}
                type={RiskOrderDetailDialogType.HISTORY_ORDER}
            />
            {/* ai数据智能分析弹窗 */}
            <AiAnalysisDialog
                open={riskOrderListState.aiAnalysisDialogOpen}
                stringId={riskOrderListState.stringId}
                onClose={() => riskOrderListState.closeAiAnalysisDialog()}
            />
        </Box>
    );
});

export default RiskOrderListCloud;
